import { FEED_DISCLAIMER } from '@fruuit/shared'
import {
  Callout,
  Card,
  EmptyState,
  LoadingRegion,
  PageHeader,
  Skeleton,
  StatGroup,
  StatTile,
  StatusBadge,
  UiLink,
} from '@fruuit/ui'
import { BellRing, Building, CircleCheck, KeyRound, ShieldCheck, Users } from 'lucide-react'
import { errorMessage } from '../../api/client'
import { useAlerts } from '../../features/alerts/api'
import { AlertSummaryList } from '../../features/alerts/components'
import { useFeeds } from '../../features/feeds/api'
import { formatDateTime, formatRelative, plural } from '../../features/feeds/format'
import { ProviderCard } from '../../features/feeds/ProviderCard'
import { useAdminOverview } from '../../features/subscriptions/api'
import { useTitle } from '../../lib/useTitle'

const AUDIT_LABEL = { ok: 'Verified', failed: 'Failed', unknown: 'Not yet verified' } as const
const AUDIT_TONE = { ok: 'complete', failed: 'failed', unknown: 'partial' } as const

/** M-05 Overview: platform health, ingest status per source and open alerts. */
export function OverviewPage() {
  useTitle('Overview', 'Admin')
  const overview = useAdminOverview()
  const feeds = useFeeds()
  const alerts = useAlerts({ status: 'open' }, null, 5)
  const data = overview.data

  return (
    <>
      <PageHeader
        eyebrow="Platform admin"
        title="Platform health at a glance."
        description="Who can sign in, how the data sources are delivering, and what needs attention."
      />

      {overview.isError ? (
        <Callout tone="danger" role="alert" className="mb-6">
          {errorMessage(overview.error)}
        </Callout>
      ) : null}

      <StatGroup>
        <StatTile
          label="Investor orgs"
          icon={<Building />}
          value={data ? data.orgs.investor : '…'}
          note={
            data
              ? data.orgs.suspended > 0
                ? `${plural(data.orgs.suspended, 'org')} suspended`
                : 'None suspended'
              : undefined
          }
          href="/admin/orgs"
        />
        <StatTile
          label="Users"
          icon={<Users />}
          value={data ? data.users.active : '…'}
          note={
            data
              ? `${data.users.invited} invited · ${data.users.signed_in_7d} signed in this week`
              : undefined
          }
          href="/admin/users"
        />
        <StatTile
          label="Active sessions"
          icon={<KeyRound />}
          value={data ? (data.sessions_active ?? '—') : '…'}
          note={data?.sessions_active === null ? 'Session store unavailable' : 'Across all areas'}
        />
        <StatTile
          label="Open alerts"
          icon={<BellRing />}
          value={data ? data.alerts.open : '…'}
          note={
            data ? `${data.alerts.critical} critical · ${data.alerts.warning} warning` : undefined
          }
          href="/admin/alerts"
        />
      </StatGroup>

      <section aria-labelledby="sources-heading" className="mt-10">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="sources-heading" className="text-lg font-semibold text-ink">
              Data sources
            </h2>
            <p className="mt-1 text-sm text-sage">
              Ingest status per provider. Failed, partial and late runs are coverage gaps.
            </p>
          </div>
          <UiLink href="/admin/feeds" className="text-sm font-semibold text-teal hover:underline">
            All feed runs
          </UiLink>
        </div>
        {feeds.isPending ? (
          <LoadingRegion label="Loading data sources" className="grid gap-4 lg:grid-cols-3">
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} className="h-80 w-full rounded-lg" />
            ))}
          </LoadingRegion>
        ) : feeds.isError ? (
          <Callout tone="danger" role="alert">
            {errorMessage(feeds.error)}
          </Callout>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            {feeds.data.providers.map((provider) => (
              <ProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card
          title="Open alerts"
          eyebrow="Needs attention"
          actions={
            <UiLink href="/admin/alerts" className="font-semibold text-teal hover:underline">
              View all
            </UiLink>
          }
        >
          {alerts.isPending ? (
            <LoadingRegion label="Loading alerts" />
          ) : alerts.isError ? (
            <Callout tone="danger" role="alert">
              {errorMessage(alerts.error)}
            </Callout>
          ) : alerts.data.items.length === 0 ? (
            <EmptyState
              variant="plain"
              icon={<CircleCheck />}
              title="No open alerts"
              description="Failed and late deliveries, partial runs and high-severity signals on held issuers appear here."
            />
          ) : (
            <AlertSummaryList alerts={alerts.data.items} />
          )}
        </Card>

        <Card title="Platform checks" eyebrow="Operations">
          {data ? (
            <dl className="flex flex-col gap-4 text-sm">
              <div className="flex items-start justify-between gap-3">
                <dt className="flex items-center gap-2 font-semibold text-ink">
                  <ShieldCheck aria-hidden="true" className="size-4 text-teal-500" />
                  Audit chains
                </dt>
                <dd className="flex flex-col items-end gap-1 text-right">
                  <StatusBadge
                    status={AUDIT_TONE[data.audit.status]}
                    label={AUDIT_LABEL[data.audit.status]}
                  />
                  <span className="text-xs text-sage">
                    {data.audit.last_verified_at
                      ? `Last run ${formatRelative(data.audit.last_verified_at)}`
                      : 'No verification recorded'}
                    {data.audit.chains !== null ? ` · ${plural(data.audit.chains, 'chain')}` : ''}
                  </span>
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="font-semibold text-ink">Access granted</dt>
                <dd className="text-right text-sage">
                  {plural(data.subscriptions_active, 'subscription')}
                  <br />
                  {plural(data.entitlements_active, 'dataset entitlement')}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="font-semibold text-ink">Signals awaiting triage</dt>
                <dd className="text-right text-sage">
                  {data.signals.new} new · {data.signals.mapping_review} in mapping review
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-ink">Scheduled jobs</dt>
                <dd className="mt-2">
                  {data.jobs.length === 0 ? (
                    <p className="text-sage">No cron runs recorded yet.</p>
                  ) : (
                    <ul className="divide-y divide-line-soft rounded-md border border-line-soft">
                      {data.jobs.map((job) => (
                        <li
                          key={`${job.job}-${job.started_at}`}
                          className="flex items-center justify-between gap-3 px-3 py-2"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-ink">{job.job}</span>
                            <span
                              className="text-xs text-sage"
                              title={formatDateTime(job.started_at)}
                            >
                              {formatRelative(job.started_at)}
                            </span>
                          </span>
                          <StatusBadge status={job.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
              </div>
            </dl>
          ) : (
            <LoadingRegion label="Loading platform checks" />
          )}
        </Card>
      </div>

      <Callout tone="note" className="mt-8">
        {feeds.data?.disclaimer ?? FEED_DISCLAIMER}
      </Callout>
    </>
  )
}
